/**
 * Auth-page backdrop. Layered composition designed for a "serious SaaS"
 * feel — flat dark base + two soft corner glows for depth + a fine dotted
 * grid for texture. No decorative artwork; everything reads as structure.
 *
 * Stroke / glow colours follow `--color-xk-accent` so brand-override
 * theming flows through automatically.
 */
export function FlowBackground() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {/* Solid base — sits below the gradient layers so any subpixel gap is
          still on-brand instead of falling through to <body>. */}
      <div
        className="absolute inset-0"
        style={{ background: 'var(--color-xk-navy-mid, #0a0613)' }}
      />

      {/* Soft purple glow anchored to the top-left. Wide spread (60% of the
          viewport) and very low peak opacity keep the feel calm. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 55% at 0% 0%, color-mix(in srgb, var(--color-xk-accent) 18%, transparent) 0%, transparent 60%)',
        }}
      />

      {/* Cooler counter-glow at the bottom-right with the dimmed accent so
          the composition has tonal contrast without a second hue. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 65% 60% at 100% 100%, color-mix(in srgb, var(--color-xk-accent-dim, var(--color-xk-accent)) 12%, transparent) 0%, transparent 55%)',
        }}
      />

      {/* Centre lift — a tiny brightness bump in the middle so the form area
          (which sits roughly here) reads as elevated, not buried. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 35% 45%, color-mix(in srgb, var(--color-xk-accent) 5%, transparent) 0%, transparent 70%)',
        }}
      />

      {/* Fine dot grid — 36px spacing, ~14% accent opacity. Provides the
          "engineered surface" texture you see on Linear / Vercel / Stripe
          dashboards. The 1px dots stay crisp on hi-DPI screens. */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, color-mix(in srgb, var(--color-xk-accent) 24%, transparent) 1px, transparent 1.5px)',
          backgroundSize: '36px 36px',
          opacity: 0.55,
          maskImage:
            'radial-gradient(ellipse 80% 70% at 50% 45%, black 30%, transparent 90%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 70% at 50% 45%, black 30%, transparent 90%)',
        }}
      />

      {/* Edge vignette to push focus toward the centre and stop the dot grid
          from feeling tile-perfect at the corners. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 100% 100% at 50% 50%, transparent 55%, rgb(0 0 0 / 0.55) 100%)',
        }}
      />
    </div>
  );
}
